import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Products.css";
import { FiArrowUpRight } from "react-icons/fi";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getBoards } from "../../../../api/boards";

import arrowLeft from "../../../../assets/icons/left.svg";
import arrowRight from "../../../../assets/icons/right.svg";

import gyp1 from "../../../../assets/images/home/products/whiteGyps.png";
import gyp2 from "../../../../assets/images/home/products/greenGyps.png";
import gyp3 from "../../../../assets/images/home/products/greenGyps.png";
import gyp4 from "../../../../assets/images/home/products/pinkGyps.png";
import gyp5 from "../../../../assets/images/home/products/fmr.png";
import gyp6 from "../../../../assets/images/home/products/exterior.png";
import { resolveMediaUrl } from "../../../../utils/media";

const fallbackProducts = {
    tag: "GYPSUM PRODUCTS",
    titleLine1: "Products for",
    titleLine2: "Every Need",
    openProductText: "Open product",
    item1Title: "Regular Light Weight",
    item1Description:
        "Regular gypsum board is a factory-manufactured, paper-faced panel designed for standard interior applications.",
    item1Image: gyp1,
    item1Link: "/products/boards/regular",
    item2Title: "Moisture",
    item2Description: "Moisture resistant gypsum board for wet areas.",
    item2Image: gyp2,
    item2Link: "/products/boards/moisture",
    item3Title: "Moisture",
    item3Description: "Moisture resistant gypsum board for wet areas.",
    item3Image: gyp3,
    item3Link: "/products/boards/mousture",
    item4Title: "Fire",
    item4Description: "Fire-resistant gypsum board with enhanced safety.",
    item4Image: gyp4,
    item4Link: "/products/boards/fire",
    item5Title: "FMR",
    item5Description: "FMR gypsum board combines fire and moisture resistance in one board.",
    item5Image: gyp5,
    item5Link: "/products/boards/fmr",
    item6Title: "Exterior",
    item6Description: "Exterior gypsum board designed for outdoor and facade applications.",
    item6Image: gyp6,
    item6Link: "/products/boards/exterior",
};

const hasSectionData = (section) => {
    if (!section || typeof section !== "object") return false;
    return Object.values(section).some((value) => {
        if (value === null || typeof value === "undefined") return false;
        if (typeof value === "string") return value.trim().length > 0;
        return true;
    });
};

const slugifyBoard = (value) =>
    String(value || "")
        .toLowerCase()
        .trim()
        .replace(/&/g, "and")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

const BOARD_RENDER_CONFIG = [
    { label: "Regular Board", aliases: ["regular board", "regular"] },
    { label: "AL AHLI H3 MR", aliases: ["al ahli h3 mr", "h3 mr"] },
    { label: "AQUAGUARD", aliases: ["aquaguard"] },
    { label: "Fire Board", aliases: ["fire board", "fire"] },
    { label: "FMR Board", aliases: ["fmr board", "fmr"] },
    { label: "Shieldex", aliases: ["shieldex"] },
];

const normalizeBoardName = (value) =>
    String(value || "")
        .toLowerCase()
        .replace(/[_-]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

const getBoardRenderConfigIndex = (title) => {
    const normalizedTitle = normalizeBoardName(title);
    return BOARD_RENDER_CONFIG.findIndex((config) =>
        config.aliases.some((alias) => normalizedTitle.includes(normalizeBoardName(alias)))
    );
};

const Products = ({ content }) => {
    const { i18n } = useTranslation();
    const sliderRef = useRef(null);
    const isRtl = i18n.dir(i18n.language) === "rtl";
    const productsContent = hasSectionData(content) ? content : fallbackProducts;
    const [apiBoards, setApiBoards] = useState([]);

    useEffect(() => {
        let isMounted = true;
        const loadBoards = async () => {
            try {
                const data = await getBoards({ language: String(i18n.language || "en").toLowerCase() });
                if (isMounted) {
                    setApiBoards(Array.isArray(data) ? data : []);
                }
            } catch (_error) {
                if (isMounted) {
                    setApiBoards([]);
                }
            }
        };
        loadBoards();
        return () => {
            isMounted = false;
        };
    }, [i18n.language]);

    const products = useMemo(() => {
        const fallbackImages = [gyp1, gyp2, gyp3, gyp4, gyp5, gyp6];
        const fallbackCards = [
            {
                title: fallbackProducts.item1Title,
                description: fallbackProducts.item1Description,
                image: fallbackProducts.item1Image,
                to: fallbackProducts.item1Link,
                fallbackImage: fallbackProducts.item1Image,
            },
            {
                title: fallbackProducts.item2Title,
                description: fallbackProducts.item2Description,
                image: fallbackProducts.item2Image,
                to: fallbackProducts.item2Link,
                fallbackImage: fallbackProducts.item2Image,
            },
            {
                title: fallbackProducts.item3Title,
                description: fallbackProducts.item3Description,
                image: fallbackProducts.item3Image,
                to: fallbackProducts.item3Link,
                fallbackImage: fallbackProducts.item3Image,
            },
            {
                title: fallbackProducts.item4Title,
                description: fallbackProducts.item4Description,
                image: fallbackProducts.item4Image,
                to: fallbackProducts.item4Link,
                fallbackImage: fallbackProducts.item4Image,
            },
            {
                title: fallbackProducts.item5Title,
                description: fallbackProducts.item5Description,
                image: fallbackProducts.item5Image,
                to: fallbackProducts.item5Link,
                fallbackImage: fallbackProducts.item5Image,
            },
            {
                title: fallbackProducts.item6Title,
                description: fallbackProducts.item6Description,
                image: fallbackProducts.item6Image,
                to: fallbackProducts.item6Link,
                fallbackImage: fallbackProducts.item6Image,
            },
        ];

        const mapped = (Array.isArray(apiBoards) ? apiBoards : [])
            .map((fields, idx) => {
                const rawSlug =
                    fields?.slug || fields?.boardType || fields?.type || fields?.name || fields?.title || fields?.id;
                const slug = slugifyBoard(rawSlug);
                const fallbackId = String(fields?.id || "").trim();
                const to = slug
                    ? `/products/boards/${slug}`
                    : fallbackId
                      ? `/products/boards/${encodeURIComponent(fallbackId)}`
                      : "/products/boards";

                const resolvedTitle = fields?.name || fields?.title || fields?.boardType || "Board";
                const configIndex = getBoardRenderConfigIndex(resolvedTitle);
                const renderedTitle = configIndex >= 0 ? BOARD_RENDER_CONFIG[configIndex].label : resolvedTitle;

                return {
                    title: renderedTitle,
                    description:
                        fields?.heroDescription ||
                        "Gypsum board technical details are available in the product page.",
                    image: resolveMediaUrl(fields?.imageUrl) || fallbackImages[idx % fallbackImages.length],
                    to,
                    active: false,
                    fallbackImage: fallbackImages[idx % fallbackImages.length],
                };
            })
            .filter((item) => item.title);

        mapped.sort((a, b) => {
            const aTitle = normalizeBoardName(a.title);
            const bTitle = normalizeBoardName(b.title);
            const aIndex = getBoardRenderConfigIndex(a.title);
            const bIndex = getBoardRenderConfigIndex(b.title);
            const aRank = aIndex >= 0 ? aIndex : Number.MAX_SAFE_INTEGER;
            const bRank = bIndex >= 0 ? bIndex : Number.MAX_SAFE_INTEGER;
            if (aRank !== bRank) return aRank - bRank;
            return aTitle.localeCompare(bTitle);
        });

        if (!mapped.length) return fallbackCards;
        return mapped;
    }, [apiBoards]);

    const scroll = (direction) => {
        const container = sliderRef.current;
        const scrollAmount = 300;
        const logicalDirection = isRtl
            ? (direction === "left" ? "right" : "left")
            : direction;

        container.scrollBy({
            left: logicalDirection === "left" ? -scrollAmount : scrollAmount,
            behavior: "smooth",
        });
    };

    return (
        <section className="products">
            <div className="products-header">
                <span className="products-tag tag">{productsContent.tag || "GYPSUM PRODUCTS"}</span>

                <h2>
                    {productsContent.titleLine1 || "Products for"} <span>{productsContent.titleLine2 || "Every Need"}</span>
                </h2>
            </div>

            <div className="products-slider-wrapper">
                <div className="products-slider" ref={sliderRef}>
                    {products.map((item, index) => (
                        <Link
                            key={index}
                            className={`product-card ${item.active ? "active" : ""}`}
                            to={item.to || "/products"}
                        >
                            <div className="product-inner">

                                <div className="product-top">
                                    <div className="product-image">
                                        <img
                                            src={item.image || item.fallbackImage}
                                            alt={item.title}
                                            onError={(e) => {
                                                if (e.currentTarget.src !== item.fallbackImage) {
                                                    e.currentTarget.src = item.fallbackImage;
                                                }
                                            }}
                                        />
                                    </div>
                                </div>

                                <div className="product-content">
                                    <h3>{item.title}</h3>

                                    <p>{item.description}</p>

                                    <div className="product-link">
                                        {productsContent.openProductText || "Open product"} <FiArrowUpRight />
                                    </div>
                                </div>

                            </div>
                        </Link>
                    ))}
                </div>

                <div className="products-arrows">
                    <button
                        type="button"
                        onClick={() => scroll("left")}
                        aria-label="Scroll products left"
                    >
                        <img src={isRtl ? arrowRight : arrowLeft} alt="" draggable={false} />
                    </button>
                    <button
                        type="button"
                        onClick={() => scroll("right")}
                        aria-label="Scroll products right"
                    >
                        <img src={isRtl ? arrowLeft : arrowRight} alt="" draggable={false} />
                    </button>
                </div>
            </div>
        </section>
    );
};

export default Products;